import { useQuery } from '@tanstack/react-query';

import type { ImportJob, ImportJobStatus, ImportTarget } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { importsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

export interface GetImportJobsParams {
	target?: ImportTarget;
	status?: ImportJobStatus;
	limit?: number;
}

/**
 * API function to get import jobs
 * @param servicesProviderId - Required services provider ID
 * @param params - Optional filters (target, limit)
 */
export const getImportJobs = async (
	servicesProviderId: string,
	params?: GetImportJobsParams,
): Promise<ImportJob[]> => {
	const queryParams = new URLSearchParams();
	if (params?.target) {
		queryParams.append('target', params.target);
	}
	if (params?.limit !== undefined) {
		queryParams.append('limit', params.limit.toString());
	}
	if (params?.status) {
		queryParams.append('status', params.status);
	}
	const queryString = queryParams.toString();
	const url = `${importsEndpoints.base(servicesProviderId)}${
		queryString ? `?${queryString}` : ''
	}`;

	const response = await apiClient.get<ImportJob[]>(url);
	return response.data;
};

/**
 * Hook for fetching import jobs with optional filters
 * Note: Errors are handled by a local error boundary, not the route error boundary
 */
export const useGetImportJobs = (params?: GetImportJobsParams) => {
	const currentServicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.inventory.imports(currentServicesProviderId, params).queryKey,
		queryFn: async () => {
			if (!currentServicesProviderId) {
				throw new Error('Unable to fetch import jobs without a services provider ID.');
			}

			return getImportJobs(currentServicesProviderId, params);
		},
		enabled: Boolean(currentServicesProviderId),
		staleTime: 5 * 60 * 1000, // 5 minutes
		throwOnError: true, // Throw to local error boundary wrapping ImportJobSection
	});
};
